/**
 * HTC Theme-Native VIP Welcome Modal & Lead Magnet (HTC-R-05)
 * - Pure Vanilla JavaScript (<2 KiB)
 * - Zero external tracking or SaaS bloat
 * - 14-day localStorage frequency capping
 * - Standard Shopify Native Form Submission (100% Reliable Customer Opt-in)
 * - Double Opt-in Verification Gate (Prevents fake emails from seeing promo code)
 * - Accessible Modal Focus Trap & "Thank You" Dismissal
 * - WCAG 2.1 AA Compliant Keyboard & Focus Trapping
 */

(function () {
  'use strict';

  const STORAGE_DISMISSED_KEY = 'htc_vip_modal_dismissed';
  const STORAGE_SUBSCRIBED_KEY = 'htc_vip_modal_subscribed';
  const DISMISS_SUPPRESSION_DAYS = 14;
  const DESKTOP_TIME_DELAY_MS = 10000;
  const MOBILE_TIME_DELAY_MS = 12000;
  const SCROLL_DEPTH_THRESHOLD = 0.4; // 40% scroll on mobile

  let modalEl = null;
  let cardEl = null;
  let headerEl = null;
  let formWrapEl = null;
  let formEl = null;
  let successEl = null;
  let emailInputEl = null;
  let thankYouBtnEl = null;
  let previousActiveElement = null;
  let isTriggered = false;
  let isClosed = false;

  function isSuppressed() {
    try {
      // 1. If customer already subscribed, never show again
      if (localStorage.getItem(STORAGE_SUBSCRIBED_KEY) === 'true') {
        return true;
      }

      // 2. If customer dismissed within the suppression window, keep hidden
      const dismissedTimestamp = localStorage.getItem(STORAGE_DISMISSED_KEY);
      if (dismissedTimestamp) {
        const elapsedDays = (Date.now() - parseInt(dismissedTimestamp, 10)) / (1000 * 60 * 60 * 24);
        if (elapsedDays < DISMISS_SUPPRESSION_DAYS) {
          return true;
        }
      }

      // 3. Never show on cart or checkout pages
      const path = window.location.pathname;
      if (path.includes('/cart') || path.includes('/checkout')) {
        return true;
      }
    } catch (e) {
      // Graceful fallback if localStorage is disabled in private/incognito mode
      return false;
    }
    return false;
  }

  function openModal() {
    if (isTriggered || isClosed || !modalEl) return;
    if (
      document.body.classList.contains('cart-drawer-open') ||
      document.body.classList.contains('sizing-modal-open') ||
      document.body.classList.contains('pet-quiz-open') ||
      document.querySelector('cart-drawer.active, .jdgm-write-review-modal:not([style*="display: none"]):not([style*="display:none"])')
    ) {
      return;
    }

    isTriggered = true;
    previousActiveElement = document.activeElement;

    // Teleport to body if not direct child (Rule 3.3)
    if (modalEl.parentNode !== document.body) {
      document.body.appendChild(modalEl);
    }

    modalEl.removeAttribute('hidden');
    document.body.classList.add('htc-vip-modal-open');

    // Focus Thank You button if in success state, otherwise focus email input or card
    setTimeout(() => {
      if (successEl && successEl.style.display !== 'none' && thankYouBtnEl) {
        thankYouBtnEl.focus();
      } else if (emailInputEl) {
        emailInputEl.focus();
      } else if (cardEl) {
        cardEl.focus();
      }
    }, 150);

    // Bind keyboard escape listener
    document.addEventListener('keydown', handleKeyDown);
  }

  function closeModal() {
    if (!modalEl || modalEl.hasAttribute('hidden')) return;

    isClosed = true;
    modalEl.setAttribute('hidden', '');
    document.body.classList.remove('htc-vip-modal-open');
    document.removeEventListener('keydown', handleKeyDown);

    try {
      // If modal was closed from the success state or via Thank You button, mark as subscribed permanently
      if (successEl && successEl.style.display !== 'none') {
        localStorage.setItem(STORAGE_SUBSCRIBED_KEY, 'true');
      } else if (localStorage.getItem(STORAGE_SUBSCRIBED_KEY) !== 'true') {
        localStorage.setItem(STORAGE_DISMISSED_KEY, Date.now().toString());
      }
    } catch (e) {}

    if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
      previousActiveElement.focus();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      closeModal();
      return;
    }

    // Accessible Focus Trap inside card
    if (e.key === 'Tab' || e.keyCode === 9) {
      if (!cardEl) return;
      const focusables = cardEl.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;

      const firstItem = focusables[0];
      const lastItem = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstItem) {
          e.preventDefault();
          lastItem.focus();
        }
      } else {
        if (document.activeElement === lastItem) {
          e.preventDefault();
          firstItem.focus();
        }
      }
    }
  }

  function handleFormSubmit(e) {
    if (!formEl || !emailInputEl) return;

    const email = emailInputEl.value.trim();
    if (!email || !email.includes('@')) {
      e.preventDefault();
      return;
    }

    const submitBtn = document.getElementById('HtcVipSubmitBtn');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span>Sending Verification Email...</span>';
    }

    // Allow native form submission to proceed directly to Shopify backend.
    // Native submission ensures 100% compliance with Shopify bot protection & customer email triggers.
    // NOTE: Promo code is NOT applied here; user must verify email first.
  }

  function showSuccessState() {
    if (headerEl) headerEl.style.display = 'none';
    if (formWrapEl) formWrapEl.style.display = 'none';
    if (successEl) successEl.style.display = 'block';
  }

  function setupTriggers() {
    const isMobile = window.innerWidth <= 749;

    // Trigger A: Timed popup
    const delay = isMobile ? MOBILE_TIME_DELAY_MS : DESKTOP_TIME_DELAY_MS;
    setTimeout(() => {
      openModal();
    }, delay);

    // Trigger B: Desktop Exit Intent (cursor leaves top window boundary)
    if (!isMobile) {
      document.addEventListener('mouseleave', function onMouseLeave(e) {
        if (e.clientY <= 12 && !isTriggered && !isClosed) {
          openModal();
          document.removeEventListener('mouseleave', onMouseLeave);
        }
      });
    }

    // Trigger C: Mobile Scroll Depth (40% down page)
    if (isMobile) {
      let scrollThrottled = false;
      window.addEventListener('scroll', function onScroll() {
        if (scrollThrottled || isTriggered || isClosed) return;
        scrollThrottled = true;
        setTimeout(() => {
          scrollThrottled = false;
          const scrollPosition = window.scrollY + window.innerHeight;
          const totalHeight = document.documentElement.scrollHeight;
          if (totalHeight > 0 && scrollPosition / totalHeight >= SCROLL_DEPTH_THRESHOLD) {
            openModal();
            window.removeEventListener('scroll', onScroll);
          }
        }, 200);
      }, { passive: true });
    }
  }

  function bindEvents() {
    // Close actions (close button, Thank You button, Continue Browsing, and backdrop click)
    const closeTriggers = modalEl.querySelectorAll('[data-action="close"]');
    closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', closeModal);
    });

    // Form submission
    if (formEl) {
      formEl.addEventListener('submit', handleFormSubmit);
    }
  }

  function init() {
    modalEl = document.getElementById('HtcVipModal');
    if (!modalEl) return;

    cardEl = modalEl.querySelector('.htc-vip-modal__card');
    headerEl = document.getElementById('HtcVipHeader');
    formWrapEl = document.getElementById('HtcVipFormWrap');
    formEl = document.getElementById('HtcVipCustomerForm');
    successEl = document.getElementById('HtcVipSuccess');
    emailInputEl = document.getElementById('HtcVipEmail');
    thankYouBtnEl = document.getElementById('HtcVipThankYouBtn');

    bindEvents();

    // 0. If Fit Quiz email form just posted, let Pet Fit Quiz handle the reload instead of opening 10% VIP modal
    try {
      const quizSavedRaw = sessionStorage.getItem('htc_quiz_saved_state');
      if (quizSavedRaw && quizSavedRaw.includes('"pendingPost":true')) {
        return;
      }
    } catch (e) {}

    // 1. Check if server returned success after native form submission
    const isPostedSuccess = Boolean(
      window.__htc_vip_posted_success ||
      (window.location.search && window.location.search.includes('customer_posted=true'))
    );

    if (isPostedSuccess) {
      // Clean URL parameter so refreshing doesn't re-trigger success
      if (window.history && window.history.replaceState && window.location.search.includes('customer_posted=true')) {
        const cleanUrl = window.location.pathname + window.location.search.replace(/[?&]customer_posted=true/, '').replace(/^&/, '?') + window.location.hash;
        window.history.replaceState({}, document.title, cleanUrl || window.location.pathname);
      }

      showSuccessState();
      openModal();
      return;
    }

    // 2. Check if server returned an error (e.g. invalid email)
    if (window.__htc_vip_posted_error) {
      openModal();
      return;
    }

    // 3. Normal suppression check (subscribed or dismissed recently)
    if (isSuppressed()) {
      return;
    }

    // 4. Set up timers, exit-intent, and scroll triggers
    setupTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
